import {
  UsersThree,
  ChartBar,
  Gauge,
  Kanban,
  ArrowRight,
} from "@phosphor-icons/react";

const teamFeatures = [
  {
    icon: ChartBar,
    title: "Team Analytics",
    description:
      "Real-time dashboards showing individual and team performance, task completion rates, and trend analysis.",
  },
  {
    icon: Gauge,
    title: "Capacity Planning",
    description:
      "Visualize bandwidth across teams. AI recommends optimal task distribution based on skills and availability.",
  },
  {
    icon: Kanban,
    title: "Shared Workspaces",
    description:
      "Cross-functional project boards with real-time collaboration. Every team member sees the big picture.",
  },
];

export function TeamCollaborationSection() {
  return (
    <section className="relative overflow-hidden bg-slate-50 dark:bg-slate-950 py-24 lg:py-32">
      <div className="absolute inset-0 tech-grid-bg opacity-10 dark:opacity-30" />
      <div className="absolute top-0 right-0 h-125 w-125 rounded-full bg-primary-500/5 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-5 items-center">
          {/* Left: Mock UI (3 cols) */}
          <div className="lg:col-span-3 relative">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-6 shadow-2xl backdrop-blur-sm">
              {/* Header bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <UsersThree
                    size={20}
                    weight="fill"
                    className="text-primary-400"
                  />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    Team Overview
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
                  <div className="h-2.5 w-2.5 rounded-full bg-[#3B7AE0]/60" />
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500/60" />
                </div>
              </div>

              {/* Team member rows */}
              {[
                {
                  name: "Sarah Chen",
                  role: "Lead Engineer",
                  load: 78,
                  tasks: 12,
                  color: "bg-primary-500",
                },
                {
                  name: "Marcus Johnson",
                  role: "Designer",
                  load: 45,
                  tasks: 6,
                  color: "bg-emerald-500",
                },
                {
                  name: "Priya Patel",
                  role: "PM",
                  load: 92,
                  tasks: 18,
                  color: "bg-red-500",
                },
                {
                  name: "James Torres",
                  role: "Backend Dev",
                  load: 61,
                  tasks: 9,
                  color: "bg-[#3B7AE0]",
                },
              ].map((member) => (
                <div
                  key={member.name}
                  className="flex items-center gap-4 py-3.5 border-t border-slate-200 dark:border-slate-800"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-bold text-slate-600 dark:text-slate-300">
                    {member.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {member.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {member.tasks} tasks
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${member.color} transition-all duration-700`}
                          style={{ width: `${member.load}%` }}
                        />
                      </div>
                      <span
                        className={`text-xs font-medium ${member.load > 85 ? "text-red-400" : member.load > 70 ? "text-primary-400" : "text-emerald-400"}`}
                      >
                        {member.load}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* AI insight */}
              <div className="mt-4 rounded-xl border border-primary-500/20 bg-primary-500/5 p-3">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 h-4 w-4 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary-400 animate-pulse-glow" />
                  </div>
                  <p className="text-xs text-primary-300/80 leading-relaxed">
                    <span className="font-semibold text-primary-300">
                      AI insight:
                    </span>{" "}
                    Priya is at 92% capacity. Consider reassigning 3
                    low-priority tasks to Marcus who has bandwidth.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content (2 cols) */}
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-500/30 bg-primary-500/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-primary-400 uppercase">
              <UsersThree size={14} weight="fill" />
              Team Collaboration
            </div>

            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
              See your team like{" "}
              <span className="gradient-text">never before</span>
            </h2>

            <p className="mt-4 text-slate-600 dark:text-slate-400 leading-relaxed">
              Real-time visibility into every team member's workload, skills,
              and availability. Make data-driven decisions about task
              allocation.
            </p>

            <div className="mt-8 space-y-6">
              {teamFeatures.map((feat) => (
                <div key={feat.title} className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-500/10 border border-primary-500/20">
                    <feat.icon
                      size={20}
                      weight="duotone"
                      className="text-primary-400"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      {feat.title}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                      {feat.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#pricing"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary-400 transition-colors hover:text-primary-300"
            >
              Explore team features
              <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
